import { useCallback, useMemo } from "react";
import { useGetDatasetsForReadacrossReadacrossDatasetsGet, useGetDatasetTagsDatasetTagsGet, useGetModelFamiliesReadacrossModelFamilyDatasetsGet, useGetModelFamilyTagsModelFamilyTagsGet, useGetSupportedModelMethodsModelTrainSupportedModelMethodsGet } from "../../../../api/generated/fastAPI";
import { DatasetListResponse, GetDatasetsForReadacrossReadacrossDatasetsGetParams, GetModelFamiliesReadacrossModelFamilyDatasetsGetParams, ModelFamilyDatasetListResponse } from "../../../../api/models";
import useDataParams from "../../../../hooks/useDataParams";
import FilterBar, { FilterBarType } from "../../../../components/Filtering/FilterBar";
import { defaultFilters } from "../../../../utils/supplementalData";
import { ReactComponent as ModelIcon } from '../../../../assets/Icons/navigation/Model.svg';
import PaginatedSelectList, { SelectListOption } from "../../../../components/Filtering/PaginatedSelectList";
import SearchBar from "../../../../components/Filtering/SearchBar";

const defaultDsParams: GetDatasetsForReadacrossReadacrossDatasetsGetParams = {
    name: null,
    created_by: null,
    owner: null,
    start_date: null,
    end_date: null,
    tag_groups: null,
    sort_by: 'creation_date',
    sort_order: 'desc',
    limit: 10,
    offset: 0,
};

const defaultModelDsParams: GetModelFamiliesReadacrossModelFamilyDatasetsGetParams = {
    name: null,
    created_by: null,
    owner: null,
    start_date: null,
    end_date: null,
    tag_groups: null,
    model_methods: null,
    sort_by: 'creation_date',
    sort_order: 'desc',
    limit: 10,
    offset: 0,
}



export default function SelectInternalDataset({
    selectedDataset,
    updateDatasetId,
    selectedDSType,
}: {
    selectedDataset: { id: string, name: string } | null,
    updateDatasetId: (id: string, name: string) => void,
    selectedDSType: string,
}) {    

    const getDatasetTags = useGetDatasetTagsDatasetTagsGet();
    const getModelTags = useGetModelFamilyTagsModelFamilyTagsGet();

    const getModelMethods = useGetSupportedModelMethodsModelTrainSupportedModelMethodsGet();
    
    const modelMethods = useMemo(() => {
        if (!getModelMethods.data) return [];

        const methods = getModelMethods.data as unknown as string[];
        return methods.sort();
    }, [getModelMethods.data]);

    const defaultDSFilters: FilterBarType[] = useMemo(() => {
        if (selectedDSType === 'datasets') return defaultFilters;

        return defaultFilters.concat({
            label: 'Model Methods',
            param: 'model_methods',
            type: 'multi-select',
            isDefault: true,
            icon: <ModelIcon />,
            options: modelMethods,
        })


    }, [modelMethods, selectedDSType]);

    const {
        params,
        setParams,
        search,
        filters,
        setPaginationLimit,
        setPaginationOffset
    } = useDataParams({
        defaultParams: selectedDSType === 'dataset' ? defaultDsParams : defaultModelDsParams,
        defaultFilters: defaultDSFilters,
        tagsData: (selectedDSType === 'dataset' ? getDatasetTags.data : getModelTags.data) as unknown as { [key: string]: string[] },
    });

    const getReadacrossDatasets = useGetDatasetsForReadacrossReadacrossDatasetsGet(params as GetDatasetsForReadacrossReadacrossDatasetsGetParams);
    const getReadacrossModelDatasets = useGetModelFamiliesReadacrossModelFamilyDatasetsGet(params as GetModelFamiliesReadacrossModelFamilyDatasetsGetParams);

    const { count, availableOptions }: { count: number, availableOptions: SelectListOption[] }  = useMemo(() => {
        let count = 0;
        let availableOptions: SelectListOption[] = [];

        if (selectedDSType === 'datasets' && getReadacrossDatasets.data) {
            const { count: dsCount, datasets } = getReadacrossDatasets.data as unknown as DatasetListResponse;
            count = dsCount;
            if (datasets.length > 0) availableOptions = datasets.map(ds => ({ id: ds.file_hash!, name: ds.name ?? '' }));
        } else if (selectedDSType === 'model_families' && getReadacrossModelDatasets.data) {
            const { count: mfCount, model_family_datasets } = getReadacrossModelDatasets.data as unknown as ModelFamilyDatasetListResponse;
            count = mfCount;
            if (model_family_datasets.length) availableOptions = model_family_datasets.map(ds => ({ id: ds.dataset_hash!, name: ds.name ?? '' }));
        }

        return { count, availableOptions }
    }, [
        selectedDSType,
        getReadacrossDatasets.data,
        getReadacrossModelDatasets.data,
    ]);

    const chosenOptions: SelectListOption[] = useMemo(() => {
        if (!selectedDataset) return [];

        return [selectedDataset];
    }, [selectedDataset]);

    const addDataset = useCallback((
        id: string | number,
    ) => {
        if (availableOptions.length === 0) return;

        const chosenDataset = availableOptions.find(ds => ds.id === id.toString());

        if (chosenDataset) {
            updateDatasetId(id.toString(), chosenDataset.name);
        }
    }, [availableOptions, updateDatasetId]);

    const removeDataset = useCallback((
        id: string | number,
    ) => {
        
    }, []);

    return (
        <>
            <div className="flex flex-col gap-10">
                <div className="flex flex-col justify-start items-start gap-4">
                    <SearchBar
                        label="Search"
                        searchKey="name"
                        searchFn={search}
                    />
                    <FilterBar 
                        filters={filters}
                        setParams={setParams}
                        params={params}
                    />
                </div>
                <PaginatedSelectList 
                    availableOptions={availableOptions} 
                    chosenOptions={chosenOptions}
                    addOption={addDataset}
                    removeOption={removeDataset}
                    isMultiSelect={false}
                    isLoading={getReadacrossDatasets.isLoading || getReadacrossModelDatasets.isLoading}
                    isCountLoading={getReadacrossDatasets.isLoading || getReadacrossModelDatasets.isLoading}
                    count={count}
                    setPaginationLimit={setPaginationLimit}
                    setPaginationOffset={setPaginationOffset}
                    params={params as any}
                    showSelected={false}
                />
            </div>
        </>
    )
}